/**
 * VideoPreview Component - Webcam Video Display
 * Optimized to reduce re-renders during video streaming
 */
import React, { memo, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Video, VideoOff, Camera, AlertCircle } from "lucide-react";

const VideoPreview = memo(({ 
  isEnabled, 
  onToggle, 
  streamRef,
  cameraError,
  onCameraError,
  className = ""
}) => {
  const videoRef = useRef(null);

  // Handle video stream connection
  useEffect(() => {
    if (isEnabled && streamRef?.current && videoRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.onloadedmetadata = () => {
        videoRef.current?.play().catch(console.error);
      };
    }
  }, [isEnabled, streamRef]);

  const handleToggle = useCallback(async () => {
    if (isEnabled) {
      // Stop video
      if (streamRef?.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      onToggle(false);
    } else {
      // Start video
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            width: { ideal: 640 },
            height: { ideal: 480 },
            facingMode: "user"
          } 
        });
        if (streamRef) {
          streamRef.current = stream;
        }
        onToggle(true);
        onCameraError?.(null);
      } catch (error) {
        console.error("Camera error:", error);
        onCameraError?.(error.message || "Failed to access camera");
        onToggle(false);
      }
    }
  }, [isEnabled, streamRef, onToggle, onCameraError]);

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Video Display */}
      <div className={`relative rounded-xl overflow-hidden bg-gray-900 aspect-video ${
        isEnabled ? 'ring-2 ring-green-500' : ''
      }`}>
        {isEnabled ? (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover transform scale-x-[-1]"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
            <VideoOff className="w-12 h-12 mb-2" />
            <span className="text-sm">Camera Off</span>
          </div>
        )}

        {/* Status Indicator */}
        {isEnabled && (
          <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/50 backdrop-blur-sm px-2 py-1 rounded-full">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-xs text-white">Live</span>
          </div>
        )}
      </div>

      {/* Camera Error */}
      {cameraError && (
        <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{cameraError}</span>
        </div>
      )}

      {/* Toggle Button */}
      <Button
        onClick={handleToggle}
        variant={isEnabled ? "destructive" : "outline"}
        className="w-full"
        data-testid="video-toggle-btn"
      >
        {isEnabled ? (
          <>
            <VideoOff className="w-4 h-4 mr-2" />
            Turn Off Camera
          </>
        ) : (
          <>
            <Camera className="w-4 h-4 mr-2" />
            Enable Camera
          </>
        )}
      </Button>
    </div>
  );
});

VideoPreview.displayName = "VideoPreview";

export default VideoPreview;
